import api from "./api";

/**
 * Generic API Helper class with common HTTP methods
 * Fixed to work with Redux Saga's call() effect
 */
class ApiHelper {
  constructor() {
    //    Bind all methods to preserve 'this' context when called via Redux Saga
    this.get = this.get.bind(this);
    this.post = this.post.bind(this);
    this.put = this.put.bind(this);
    this.patch = this.patch.bind(this);
    this.delete = this.delete.bind(this);
    this.upload = this.upload.bind(this);
    this.getPaginated = this.getPaginated.bind(this);
    this.handleSuccess = this.handleSuccess.bind(this);
    this.handleError = this.handleError.bind(this);
  }

  /**
   * GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters
   * @param {Object} config - Additional axios config
   * @returns {Promise}
   */
  async get(endpoint, params = {}, config = {}) {
    try {
      console.log("API GET Request to:", endpoint, "with params:", params);
      const response = await api.get(endpoint, {
        params,
        ...config,
      });
      return this.handleSuccess(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * POST request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @param {Object} config - Additional axios config
   * @returns {Promise}
   */
  async post(endpoint, data = {}, config = {}) {
    try {
      const response = await api.post(endpoint, data, config);
      return this.handleSuccess(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * PUT request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @param {Object} config - Additional axios config
   * @returns {Promise}
   */
  async put(endpoint, data = {}, config = {}) {
    try {
      const response = await api.put(endpoint, data, config);
      return this.handleSuccess(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * PATCH request
   * @param {string} endpoint - API endpoint
   * @param {Object} data - Request body data
   * @param {Object} config - Additional axios config
   * @returns {Promise}
   */
  async patch(endpoint, data = {}, config = {}) {
    try {
      const response = await api.patch(endpoint, data, config);
      return this.handleSuccess(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * DELETE request
   * @param {string} endpoint - API endpoint
   * @param {Object} config - Additional axios config
   * @returns {Promise}
   */
  async delete(endpoint, config = {}) {
    try {
      const response = await api.delete(endpoint, config);
      return this.handleSuccess(response);
    } catch (error) {
      return this.handleError(error);
    }
  }

  /**
   * File upload request
   * @param {string} endpoint - API endpoint
   * @param {FormData} formData - Form data with files
   * @param {Function} onUploadProgress - Upload progress callback
   * @returns {Promise}
   */
  async upload(endpoint, formData, onUploadProgress = null) {
    try {
      const response = await api.post(endpoint, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: onUploadProgress
          ? (progressEvent) => {
              const percentCompleted = Math.round(
                (progressEvent.loaded * 100) / progressEvent.total
              );
              onUploadProgress(percentCompleted);
            }
          : undefined,
      });
      return this.handleSuccess(response);
    } catch (error) {
      return this.handleError(error);
    }
  }



  /**
   * Paginated GET request
   * @param {string} endpoint - API endpoint
   * @param {Object} params - Query parameters including page, limit
   * @returns {Promise}
   */
  async getPaginated(endpoint, params = {}) {
    const defaultParams = {
      page: 1,
      limit: 20,
      ...params,
    };
    return this.get(endpoint, defaultParams);
  }

  /**
   * Handle successful response
   * @param {Object} response - Axios response object
   * @returns {Object}
   */
  handleSuccess(response) {
    return {
      success: true,
      data: response.data.data || response.data,
      message: response.data.message || "Request successful",
      pagination: response.data.pagination || null,
      status: response.status,
    };
  }

  /**
   * Handle error response - Returns error object instead of throwing
   * @param {Object} error - Axios error object
   * @returns {Object}
   */
  handleError(error) {
    const errorResponse = {
      success: false,
      message:
        error.response?.data?.message || error.message || "An error occurred",
      errors: error.response?.data?.errors || null,
      status: error.response?.status || 500,
      data: null,
    };

    // Log error in development
    if (process.env.NODE_ENV === "development") {
      console.error("API Error:", errorResponse);
    }

    // Return error response instead of Promise.reject for saga compatibility
    return errorResponse;
  }

  /**
   * Cancel request token
   * @returns {Object}
   */
  getCancelToken() {
    return api.CancelToken.source();
  }

  /**
   * Set global request timeout
   * @param {number} timeout - Timeout in milliseconds
   */
  setTimeout(timeout) {
    api.defaults.timeout = timeout;
  }

  /**
   * Add request interceptor
   * @param {Function} onFulfilled - Success handler
   * @param {Function} onRejected - Error handler
   */
  addRequestInterceptor(onFulfilled, onRejected) {
    return api.interceptors.request.use(onFulfilled, onRejected);
  }

  /**
   * Add response interceptor
   * @param {Function} onFulfilled - Success handler
   * @param {Function} onRejected - Error handler
   */
  addResponseInterceptor(onFulfilled, onRejected) {
    return api.interceptors.response.use(onFulfilled, onRejected);
  }
}

// Export singleton instance
export default new ApiHelper();
